/**
 * ThemeContext — Light/Dark mode with system fallback.
 *
 * Applies data-theme="light" | "dark" on the <html> element so every CSS
 * variable + light-mode override in index.css follows automatically.
 *
 * Persistence: user's explicit choice is stored in localStorage as "ld-theme".
 * If unset, we follow the OS `prefers-color-scheme` and keep listening for
 * changes so mode-switching at the OS level Just Works.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext({
  theme: "dark",
  resolvedTheme: "dark",
  setTheme: () => {},
  toggle: () => {},
});

const STORAGE_KEY = "ld-theme";

function getSystemTheme() {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  // Sync the browser chrome color so the notch/status bar tints correctly
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", theme === "light" ? "#F8FAFC" : "#050508");
  }
}

export function ThemeProvider({ children }) {
  // theme = user-set preference ("light" | "dark" | "system")
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "system";
    return localStorage.getItem(STORAGE_KEY) || "system";
  });
  // resolvedTheme = the concrete class actually applied ("light" | "dark")
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return getSystemTheme();
  });

  // Recompute the resolved theme when user preference or OS setting changes
  useEffect(() => {
    const applyResolved = () => {
      const next = theme === "system" ? getSystemTheme() : theme;
      setResolvedTheme(next);
      applyTheme(next);
    };
    applyResolved();
    if (theme === "system" && typeof window !== "undefined") {
      const media = window.matchMedia("(prefers-color-scheme: light)");
      const handler = () => applyResolved();
      media.addEventListener?.("change", handler);
      return () => media.removeEventListener?.("change", handler);
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    if (next === "system") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const toggle = useCallback(() => {
    // Simple flip — from whatever is currently rendered, go to the other side.
    setTheme(resolvedTheme === "light" ? "dark" : "light");
  }, [resolvedTheme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
